import React, { useEffect, useState } from "react";
import { availableVolunteersJson, busyVolunteersJson, patientVolunterringHoursJson, nonPatientVolunteeringHoursJson } from "../../components/recoil/trendChartRecoil";
import TrendCards from "./dashboard/components/Cards/TrendCards";
import "./dashboard/assets/styles/DashboardStyling.css";
import StayConnected from "./dashboard/components/Cards/StayConnected";
import MainTrendChart from "./dashboard/components/Cards/MainTrendChart";
import DateRangePickerComponent from "./dashboard/components/Shared/DateRangePickerComponent";
import { getUserDisplayName } from "../utils/UserData";
import ConfigurationConstants from "../utils/configurationconstants";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { currentConfiguration } from "../../service/memory/recoil/configurationstore";
import { performPostRequest } from "../../service/network/NetworkService";
import IntentType from "../../intentexecutor/utils/IntentType";
import { useGetOrgIdentifier } from "../generic/hooks/useGetOrgIdentifier";
import { convertDateToEODTimestamp, convertDateToSODTimestamp, getCurrentTimeZone } from "../../utils/dateutils";
import { getApiUrl } from "../../service/network/UrlMapper";
import ErrorNotificationComponent from "../../components/generic/error/ErrorNotificationComponent";
import { dateRangeRecoil, isDefaultDateRangeRecoil, showDashboardDateResetButton } from "../../components/recoil/dateRangeRecoil";
import CarePlanWrapper from "./careplan/CarePlanWrapper";
import { teamList } from "../../components/recoil/teamsRecoil";

const CareCoordinatorDashboard = () => {

  const displayName = getUserDisplayName();
  const organizationIdentifier = useGetOrgIdentifier()
  const teams = useRecoilValue(teamList);

  const teamNames = teams.map(team => team?.label)

  const [trendCardData, setTrendCardData] = useState({});
  const [loading, setLoading] = useState(false);
  const configuration = useRecoilValue(currentConfiguration);

  // Feature Access Status
  const [isTrendFiltersAllowed, setTrendFilterAllowed] = useState(false);
  const [isTotalVolunteerTrendCardAllowed, setTotalVolunteerTrendCardAllowed] = useState(false);
  const [isCareHoursTrendCardAllowed, setCareHoursTrendCardAllowed] = useState(false);
  const [isSavingsTrendCardAllowed, setSavingsTrendCardAllowed] = useState(false);
  const [isInactiveTrendCardAllowed, setInactiveTrendCardAllowed] = useState(false);
  const [isTargetCardAllowed, setTargetCardAllowed] = useState(false);
  const [isStayConnectedCardAllowed, setStayConnectedCardAllowed] = useState(false);

  const [targetHours, setTargetHours] = useState(-1);
  const [wagePerHour, setWagePerHour] = useState(-1);
  const [mileage, setMileage] = useState(-1);

  const [dateRange, setDateRange] = useRecoilState(dateRangeRecoil);
  const [showResetBtn, setShowResetBtn] = useRecoilState(showDashboardDateResetButton)

  const setIsDefaultDateRange = useSetRecoilState(isDefaultDateRangeRecoil);

  const mapTrendData = (data) => {
    return data >= 0 ? data : "--";
  }

  const formattedOfficeNames = (names) => {
    if (names.length === 0) return '';
    if (names.length === 1) return ` in ${names[0]}`;
    if (names.length === 2) return ` in ${names[0]} and ${names[1]}`;
    return ` in ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  };

  useEffect(() => {
    const getDashboardGoalsData = async () => {
      const payload = {
        intent: "get_configuration",
        payload: {
          configurationKey: ["niramaya.dashboarddata.keys"],
        },
        context: {
          organizationIdentifier: organizationIdentifier
        }
      };
      
      try {
        let result = await performPostRequest(getApiUrl()[IntentType.GET_CONFIGURATION], JSON.stringify(payload));
        result = await result.json();
        
        if (result[0]?.configurationValue) result = JSON.parse(result[0]?.configurationValue);
        for (let index = 0; index < result?.length; index++) {
          if (result[index]?.key === "TARGET_HOURS") setTargetHours(result[index]?.value);
          if (result[index]?.key === "WAGE_PER_HOUR") setWagePerHour(result[index]?.value);
          if (result[index]?.key === "MILEAGE") setMileage(result[index]?.value);
        }
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    }
    
    getDashboardGoalsData();
  }, [organizationIdentifier]);
  
  const savingsCardData = "$" + mapTrendData(Math.round(trendCardData?.mileages * 100) / 100) + " Mileage";

  useEffect(() => {
    const getTrendData = async () => {
      const payload = {
        "organizationIdentifier": organizationIdentifier,
        "start_date": convertDateToSODTimestamp(dateRange[0].startDate),
        "end_date": convertDateToEODTimestamp(dateRange[0].endDate),
        "timezone": getCurrentTimeZone(),
        "miles": mileage,
        "rate_per_hour": wagePerHour,
        "teamIds": teams.map(team => team?.id) 
      };
      setLoading(true)
      try {
        const result = await performPostRequest(getApiUrl()[IntentType.GET_TREND_CHART_DATA], JSON.stringify(payload));
        const resultJson = await result.json();
        setTrendCardData(resultJson)
      } catch (error) {
        ErrorNotificationComponent(error);
      } finally {
        setLoading(false)
      }
    };

    if (wagePerHour !== -1 && mileage !== -1 && targetHours !== -1) {
      getTrendData();
    }

  }, [wagePerHour, mileage, teams, targetHours, organizationIdentifier, dateRange]);


  useEffect(() => {
    // Trend Filters
    const configurationTrendFilter = configuration[ConfigurationConstants.FEATUREACCESS_HOMEPAGE_TRENDFILTERS]
    setTrendFilterAllowed(configurationTrendFilter?.visibility)

    // Trend Total Volunteer
    const configurationTotalVolunteerTrend = configuration[ConfigurationConstants.FEATUREACCESS_HOMEPAGE_TREND_TOTALVOLUNTEERS]
    setTotalVolunteerTrendCardAllowed(configurationTotalVolunteerTrend?.visibility)

    // Trends care hour
    const configurationCareHourTrend = configuration[ConfigurationConstants.FEATUREACESS_HOMEPAGE_TREND_CAREHOURS]
    setCareHoursTrendCardAllowed(configurationCareHourTrend?.visibility)

    // Trends savings
    const configurationSavingsTrend = configuration[ConfigurationConstants.FEATUREACESS_HOMEPAGE_TREND_SAVINGS]
    setSavingsTrendCardAllowed(configurationSavingsTrend?.visibility)

    // Trends inactive
    const configurationInactiveTrend = configuration[ConfigurationConstants.FEATUREACESS_HOMEPAGE_TREND_INACTIVE]
    setInactiveTrendCardAllowed(configurationInactiveTrend?.visibility)

    // Target card 
    const configurationTargetCard = configuration[ConfigurationConstants.FEATUREACESS_HOMEPAGE_TARGET]
    setTargetCardAllowed(configurationTargetCard?.visibility)

    // Stay Connected Card 
    const configurationStayConnected = configuration[ConfigurationConstants.FEATUREACCESS_HOMEPAGE_STAY_CONNECTED]
    setStayConnectedCardAllowed(configurationStayConnected?.visibility)
  }, [configuration])

  return (
    <section style={{ margin: 30, backgroundColor: "var(--bg-color)" }}>
      <div className="welcomeText">
        <span >
          Welcome back <b>{displayName}</b>, see how your Volunteers & Patients are doing{formattedOfficeNames(teamNames)}.
        </span>
      </div>

      {isTrendFiltersAllowed ? (
        <div className="row">
          <div className="col d-flex justify-content-end align-items-center mb-3 dateSection" style={{ columnGap: "1rem" }}>
            <DateRangePickerComponent setIsDefaultDateRange={setIsDefaultDateRange} setDateRange={setDateRange} dateRange={dateRange}showResetBtn={showResetBtn} setShowResetBtn={setShowResetBtn} />
          </div>
        </div>
      ) : null}

      <div className="row card-bottomContent">
        {isTotalVolunteerTrendCardAllowed && (
          <div className='col-12 col-md-4 col-lg-3 mb-4'>
            <TrendCards iconKey="volunteerGroup" cardNumber={mapTrendData(trendCardData?.active_volunteers)} cardState={availableVolunteersJson} loading={loading} cardTitle="Active Volunteers" helperText={["activeVolunteer"]} />
          </div>
        )}
        {isInactiveTrendCardAllowed && (
          <div className='col-12 col-md-4 col-lg-3 mb-4'>
            <TrendCards iconKey="report" cardNumber={mapTrendData(trendCardData?.no_of_visits)} cardSubContent={mapTrendData(trendCardData?.ongoing_visits) + " Ongoing Visits"} cardState={nonPatientVolunteeringHoursJson} loading={loading} cardTitle="Total Visits" helperText={["totalVisits", "ongoingVisits"]} />
          </div>
        )}
        {isCareHoursTrendCardAllowed && (
          <div className='col-12 col-md-4 col-lg-3 mb-4'>
            <TrendCards iconKey="careHours" cardNumber={mapTrendData(Math.round(trendCardData?.total_care_hours * 100) / 100)} cardSubContent={mapTrendData(Math.round(trendCardData?.time_travel_hours * 100) / 100) + " Travel Hours"} cardState={busyVolunteersJson} loading={loading} cardTitle="Care Hours" helperText={["careHours", "travelHours"]} />
          </div>
        )}
        {isSavingsTrendCardAllowed && (
          <div className='col-12 col-md-4 col-lg-3 mb-4'>
            <TrendCards iconKey="dollar" cardNumber={"$" + (trendCardData?.savings >= 0 ? new Intl.NumberFormat("en-US").format(Math.round(trendCardData?.savings * 100) / 100) : "--")} cardSubContent={savingsCardData} cardState={patientVolunterringHoursJson} loading={loading} cardTitle="Savings" helperText={["savings", "totalMileage"]} />
          </div>
        )}
      </div>

      <div className="row">
        {isTargetCardAllowed && (
          <MainTrendChart loading={loading} mainTrendData={trendCardData?.target_hours_data} targetHoursSet={targetHours} />
        )}
        {isStayConnectedCardAllowed && (
          <StayConnected />
        )}
      </div>

        <CarePlanWrapper />
    </section>
  );
};

export default CareCoordinatorDashboard;
